import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import UltimatePagination from 'react-ultimate-pagination-bootstrap-4';
import { faListAlt } from '@fortawesome/pro-regular-svg-icons';
import { faSearch } from '@fortawesome/pro-solid-svg-icons';

import { Alert } from 'react-bootstrap-5';
import LoadingSpinner from 'components/ui/loading_spinner';
import PageHeader from 'components/ui/page_header';
import SearchPage from 'components/search_page';
import EmptyState from 'shared/empty_state';

import DeleteSavedSearchModal from './saved_search/delete_saved_search_modal.js';
import RequirementsSetTable from './requirements_set_table';
import FilterTopBar from './filters/filter_topbar';

import { toggleAlert } from './store/actions/ui';
import { retrieveRecords, downloadRequirementsSetsAsCsv } from './store/actions/search';
import { changePage } from './store/actions/query';
import { getPaths } from './store/selectors/paths';
import { isErrorMessage, getAlertMessage } from './store/selectors/ui';
import RequirementsSetActionsDropdown from './requirements_set_actions_dropdown';
import PeopleEmailFormModal from '../../contact/index/people/modals/people_email_form_modal';

export class RequirementsSets extends React.Component {
  static propTypes = {
    alertMessage: PropTypes.string.isRequired,
    canExport: PropTypes.bool.isRequired,
    downloadRequirementsSetsAsCsv: PropTypes.func.isRequired,
    isErrorMessage: PropTypes.bool.isRequired,
    isSearching: PropTypes.bool.isRequired,
    meta: PropTypes.shape({
      currentPage: PropTypes.number.isRequired,
      pageSize: PropTypes.number.isRequired,
      totalCount: PropTypes.number.isRequired,
      totalPages: PropTypes.number.isRequired
    }),
    paths: PropTypes.shape({
      requirementsSetsPath: PropTypes.string.isRequired
    }).isRequired,
    requirementsSets: PropTypes.array.isRequired,
    retrieveRecords: PropTypes.func.isRequired,
    toggleAlert: PropTypes.func.isRequired,
    onPageChange: PropTypes.func.isRequired
  };

  componentDidMount() {
    this.props.retrieveRecords();
  }

  componentDidUpdate(prevProps) {
    const { alertMessage } = this.props;

    if (prevProps.alertMessage != alertMessage && alertMessage) {
      setTimeout(() => {
        this.handleDismiss();
      }, 6000);
    }
  }

  handleDismiss = () => {
    this.props.toggleAlert('', false);
  }

  scrollPanel = React.createRef();

  handlePageChange = (page) => {
    const { onPageChange, retrieveRecords } = { ...this.props };
    onPageChange(page);
    retrieveRecords();
  };

  render() {
    const { alertMessage, isErrorMessage } = this.props;
    return (
      <>
        { alertMessage &&
          <Alert variant={isErrorMessage ? 'danger' : 'success'} onDismiss={this.handleDismiss}>
            <div className="ms-4">
              <span dangerouslySetInnerHTML={{ __html: alertMessage }} />
            </div>
          </Alert>
        }
        <SearchPage>
          <div className="results-tab-container h-100 d-flex flex-column">
            {this.renderPageHeader()}
            {this.renderFilterTopBar()}
            <div className="table-content h-100 d-flex flex-column overflow-y-auto">
              <div className="main-content" ref={this.scrollPanel}>
                {this.renderResults()}
                {this.renderPagination()}
              </div>
            </div>
          </div>
        </SearchPage>
        <DeleteSavedSearchModal />
        <PeopleEmailFormModal />
      </>
    );
  }

  renderFilterTopBar() {
    return (
      <FilterTopBar
        requirementsCount={this.props.meta.totalCount}
      />
    );
  }

  renderCSVButton() {
    if (!this.props.canExport) return null;

    return (
      <button
        className="btn btn-secondary"
        name="export-as-csv"
        onClick={this.props.downloadRequirementsSetsAsCsv}
      >
        Export as CSV
      </button>
    );
  }

  renderPageHeader() {
    const { requirementsSets, meta: { totalCount, pageSize, currentPage } } = this.props;

    const pageData = {
      currentPage: currentPage,
      itemCount: requirementsSets.length,
      itemSingularLabel: 'requirement',
      pageSize: pageSize,
      totalCount: totalCount
    };

    return (
      <PageHeader>
        <PageHeader.Heading
          description="Manage your Requirements"
          icon={faListAlt}
          title="Requirements"
        />
        <PageHeader.Actions>
          <PageHeader.Count
            currentPage={pageData.currentPage}
            pageSize={pageData.pageSize}
            totalCount={pageData.totalCount}
          />
          <div className="d-none d-md-flex align-items-center gap-3">
            <RequirementsSetActionsDropdown />
            {this.renderCSVButton()}
          </div>
        </PageHeader.Actions>
      </PageHeader>
    );
  }

  renderResults() {
    const { requirementsSets, isSearching } = this.props;

    if (isSearching) return <LoadingSpinner />;
    if (requirementsSets.length > 0) {
      return <RequirementsSetTable />;
    }
    return (
      <div className="p-4">
        <EmptyState
          bodyText="Search with different criteria or create a new Requirement"
          header="No results found"
          icon={faSearch}
        />
      </div>
    );
  }

  renderPagination() {
    const { requirementsSets, meta: { totalCount, totalPages, currentPage } } = { ...this.props };

    if (requirementsSets.length < totalCount) {
      return (
        <div className="text-center">
          <UltimatePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onChange={this.handlePageChange}
          />
        </div>
      );
    }
  }
}

const mapStateToProps = (state) => {
  return {
    ...state.requirementsSet,
    ...state.search,
    alertMessage: getAlertMessage(state),
    canExport: state.ui.canExport,
    isErrorMessage: isErrorMessage(state),
    paths: getPaths(state)
  };
};

const mapDispatchToProps = {
  downloadRequirementsSetsAsCsv,
  retrieveRecords,
  toggleAlert,
  onPageChange: changePage
};

export default connect(mapStateToProps, mapDispatchToProps)(RequirementsSets);
